import { withApollo } from "@apollo/client/react/hoc";
import {
  find,
  findKey,
  flatten,
  includes,
  isEqual,
  isUndefined,
  keys,
  map,
  omit,
} from "lodash";
import posthog from "posthog-js";
import PropTypes from "prop-types";
import React from "react";

// @material-ui/core components
import SleepFollowUpBillingNotice from "./sleepFollowUpBillingNotice";
import { withStyles } from "@mui/styles";

// import Grid from "@material-ui/core/Grid";
// core components
import {
  Button,
  Card,
  CardBody,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import AiCoachCallout from "./aiCoachCallout.jsx";
import ConsultationQuestion from "./consultationQuestion.jsx";
import IntakeWarningNote from "./intakeWarningNote.jsx";
import VisitProgress from "./visitProgress.jsx";

import { bugsnagClient } from "~/shared/utils/errorHandling.js";
import KickClient from "../utils/api.js";

import {
  AI_CHECK_MAX_ATTEMPTS,
  assessAnswer,
  classifyAssessError,
  questionNeedsAiCheck,
  resolveAssessOutcome,
} from "../utils/answerCheck.js";

import {
  bloodPressureMixUpMessage,
  findBloodPressureMixUp,
  findSystolicWarning,
  firstBloodPressureMixUpIndex,
  systolicWarningMessage,
} from "../utils/bloodPressureCheck.js";

import Cookies from "js-cookie";
import {
  getConsultAnswersJSON,
  getForceFlavoredBBs,
  getUpgradingTo2Pack,
  getUserID,
  getVisitEmail,
  getVisitGender,
  getVisitID,
  getVisitPhone,
  getVisitProduct,
  getVisitUSState,
  setActiveSubscriptionBySubType,
  setConsultAnswersJSON,
  setFollowUpVisitFlag,
  setIsPickup,
  setVisitID,
  setVisitProduct,
} from "../utils/visitCookies.js";

import { kickRed } from "~/shared/styles/baseStyles.js";

import {
  COLOR_KICK_BLUE,
  DB_STATUS_PAUSED,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
} from "~/shared/sharedConstants.js";

import {
  getConsultationTypeByProduct,
  isConsultationTypeInsomniaFollowUp,
  isConsultationTypeInsomniaInitial,
  isDevelopmentEnvironment,
  isFollowUpConsultationType,
  isPharmacyPickupOrderType,
  subscriptionGrantsFollowUpAccess,
  preferLiveSubscription,
} from "~/shared/utils/sharedUtils.js";

import {
  isAutoAnswered,
  skipAutoAnsweredQuestions,
} from "../utils/intakeAutoAnswer.js";

import {
  checkAndRedirectIfPendingVisitOfTypeExists,
  checkAndRedirectToFollowUpIfNeeded,
  checkAndRedirectToInitialIfNoHistory,
  ensureCheckoutSessionID,
  redirectToInitialFunnel,
} from "../utils/visitUtils.js";
import {
  getConsultationQuestionnaire,
  isQuestionnaireConsultationType,
  redirectToFunnelStart,
} from "../utils/consultationTypeGuard.js";

const addonQuestionnaires = [CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON];

function getCurrentQuestionID({ consultAnswers, questionId }) {
  if (!consultAnswers) {
    return 1;
  }
  const questionIndexes = Object.keys(consultAnswers)
    .map(Number)
    .sort((a, b) => a - b);
  let currentQuestionIndex = questionId - 1;

  if (!questionIndexes.length) {
    return 1;
  }

  if (questionIndexes[questionIndexes.length - 1] + 1 >= currentQuestionIndex) {
    // questionId is less than or equal to the last unanswered question index
    // go to questionId
    return questionId;
  } else if (
    currentQuestionIndex >
    questionIndexes[questionIndexes.length - 1] + 1
  ) {
    // questionId is greater than the last answered question.
    // Go to next unanswered question.
    return questionIndexes[questionIndexes.length - 1] + 2;
  }
}

class Consultation extends React.Component {
  constructor(props) {
    super(props);
    const consultationType = this.props.match.params.consultationType;
    const queryParams = new URLSearchParams(this.props.location.search);
    const checkoutSessionID = queryParams.get("checkoutSessionID");

    this.state = {
      consultationType,
      checkoutSessionID,
      questionId: this.props.match.params.questionId,
      questionError: "",
      refresh: false,
      isAddon: addonQuestionnaires.includes(consultationType),
      sleepRestartTerms: null,
      sleepRestartTermsLoaded: false,
      sleepRestartAccepted: false,
      sleepRestartConfirmRequested: false,
      sleepRestartError: "",
      intakeSubmitting: false,
      // Realtime AI sufficiency check (aiSufficiencyCheck questions on the PA
      // initial funnel) — all per-question, reset on every question change.
      aiCheckLoading: false,
      aiCheckAttempts: 0,
      aiGuidance: "",
      lastCheckedAnswer: null,
      lastCheckedProceed: null,
      coachSlotMinHeight: 0,
    };

    // Measures the coach slot so a re-check can pin its height (the Continue
    // button must never jump upward when the suggestion swaps to "reading").
    this.coachSlotRef = React.createRef();

    // Reject unknown slugs before rehydration or rendering needs a questionnaire.
    if (!isQuestionnaireConsultationType(consultationType)) {
      this.state.invalidConsultationType = true;
      this.state.consultAnswers = {};
      this.state.questionIndex = 0;
      redirectToFunnelStart();
      return;
    }

    // rehydrate from local storage for refreshes
    const consultAnswers = (getConsultAnswersJSON() || {})[
      `${consultationType}-${this.consultIntakeQuestionnaire().version}`
    ];

    this.state.consultAnswers = consultAnswers || {};

    this.state.questionIndex = Number(this.state.questionId) - 1;

    // enforce question index in development environment
    if (!isDevelopmentEnvironment()) {
      const currentQID = getCurrentQuestionID(this.state);
      this.state.questionIndex = currentQID - 1;
      if (this.state.questionId !== currentQID) {
        this.props.history.push(
          `/visits/${this.state.consultationType}/${currentQID}?checkoutSessionID=${this.state.checkoutSessionID}`,
        );
      }
    }

    // A refresh (or a bookmarked/pasted URL) lands here without passing through
    // componentDidUpdate, so the auto-answer guard has to run on mount too —
    // otherwise reloading on a skipped question is a way to be asked it anyway.
    const landedOn = Number(this.state.questionIndex) + 1;
    const { questionId: skipTo } = skipAutoAnsweredQuestions(
      this.consultIntakeQuestionnaire(),
      this.state.consultAnswers,
      landedOn,
      1,
    );
    if (skipTo !== landedOn) {
      this.state.questionId = skipTo;
      this.state.questionIndex = skipTo - 1;
      this.props.history.replace(
        `/visits/${this.state.consultationType}/${skipTo}?checkoutSessionID=${this.state.checkoutSessionID}`,
      );
    }

    ensureCheckoutSessionID(checkoutSessionID, this.props.history);
  }

  validateCookies() {
    const consultationType = this.props.match.params.consultationType;
    // No need to validate for follow-up consultations. We already have the data.
    if (isFollowUpConsultationType(consultationType)) {
      return;
    }
    if (!getUserID() || !getVisitEmail() || !getVisitUSState()) {
      return this.props.history.push(`/visits/${consultationType}/signup`);
    }
    if (!getVisitGender() || !getVisitPhone()) {
      return this.props.history.push(
        `/visits/${consultationType}/signup-details`,
      );
    }
  }

  async componentDidMount() {
    if (this.state.invalidConsultationType) return;
    this.validateCookies();
    this.loadSleepRestartTerms();

    // If the patient already has an in-progress visit in this clinical family, bounce them to the
    // portal (with a notice banner) before anything else — they can't start a second order in a
    // vertical while the first is still in progress.
    const pendingCheck = await checkAndRedirectIfPendingVisitOfTypeExists(
      this.props.client,
      this.state.consultationType,
      this.state.checkoutSessionID,
    );
    if (pendingCheck.redirected) return;

    // Check for existing completed visits and redirect to follow-up if needed. Reuses the
    // patientRecord visits the pending-visit guard just fetched (when available) instead of
    // refetching the same query.
    const redirectedToFollowUp = await checkAndRedirectToFollowUpIfNeeded(
      this.props.client,
      this.state.consultationType,
      this.state.checkoutSessionID,
      { preloadedVisits: pendingCheck.visits },
    );
    if (redirectedToFollowUp) {
      return;
    }

    // A follow-up funnel requires a prior initial visit — steer no-history
    // users into the matching initial funnel before they fill the form (the
    // server enforces the same rule on submit with a 409).
    const redirectedToInitial = await checkAndRedirectToInitialIfNoHistory(
      this.props.client,
      this.state.consultationType,
      this.state.checkoutSessionID,
      { preloadedVisits: pendingCheck.visits },
    );
    if (redirectedToInitial) {
      return;
    }

    // Set up navigation blocking for unsaved changes
    this.unblock = this.props.history.block(() => {
      // Check if we have unsaved changes
      const hasUnsavedChanges = this.checkForUnsavedChanges();

      if (hasUnsavedChanges) {
        // Show alert instead of browser prompt
        alert("Please click the 'Continue' button below to save your changes");
        // Return false to prevent navigation
        return false;
      }

      // Allow navigation if no unsaved changes
      return true;
    });
  }

  componentWillUnmount() {
    this.questionnaireUnmounted = true;
    this.sleepRestartTermsRequest?.abort();
    // Clean up the history blocking when component unmounts
    if (this.unblock) {
      this.unblock();
    }
  }

  loadSleepRestartTerms = async () => {
    if (!isConsultationTypeInsomniaFollowUp(this.state.consultationType)) {
      return;
    }
    const request = new AbortController();
    this.sleepRestartTermsRequest = request;
    try {
      const { data } = await KickClient.newAuthenticatedClient().get(
        "/visits/sleep-follow-up-terms",
        { signal: request.signal },
      );
      if (!request.signal.aborted) {
        this.setState(
          {
            sleepRestartTerms: data,
            sleepRestartTermsLoaded: true,
          },
          () => {
            if (this.state.sleepRestartConfirmRequested && !data?.disclosure) {
              this.saveIntake();
            }
          },
        );
      }
    } catch {
      if (!request.signal.aborted) {
        this.setState({
          sleepRestartError:
            "We couldn't load your membership details. Please refresh to continue.",
        });
      }
    }
  };

  sleepRestartNeedsAcceptance = () =>
    isConsultationTypeInsomniaFollowUp(this.state.consultationType) &&
    (!this.state.sleepRestartTermsLoaded ||
      (this.state.sleepRestartTerms?.disclosure &&
        !this.state.sleepRestartAccepted));

  declineSleepRestart = () => {
    this.setState({
      sleepRestartConfirmRequested: false,
      sleepRestartAccepted: false,
    });
  };

  acceptSleepRestart = () => {
    if (
      !this.state.sleepRestartConfirmRequested ||
      !this.state.sleepRestartTermsLoaded ||
      !this.state.sleepRestartTerms?.disclosure ||
      this.state.sleepRestartError ||
      this.intakeSubmissionInFlight
    ) {
      return;
    }
    this.setState(
      { sleepRestartAccepted: true, sleepRestartConfirmRequested: false },
      this.saveIntake,
    );
  };

  componentDidUpdate(prevProps) {
    if (this.state.invalidConsultationType) return;
    // sync questionId and questionIndex so child question component refreshes - fixes back bug
    const questionID = Number(this.props.match.params.questionId);
    const prevQuestionID = Number(prevProps.match.params.questionId);

    if (this.state.questionId != questionID) {
      // When the question changes after user confirmed navigation with unsaved changes
      if (
        prevQuestionID !== questionID &&
        this.unsavedQuestionIndex !== undefined
      ) {
        // Reset the answer of the question we navigated away from
        this.resetAnswerForQuestion(this.unsavedQuestionIndex);
        this.unsavedQuestionIndex = undefined;
      }

      // The patient can reach any already-answered question with the browser
      // back button, including one the flow auto-answered and skipped. Bounce
      // off it in whichever direction they were travelling, so a skipped
      // question is never presented — the point of skipping the autoship
      // question is that a patient changing medication cannot request a
      // recurring shipment, and a back button must not be a way around that.
      if (this.redirectPastAutoAnsweredQuestion(questionID, prevQuestionID)) {
        return;
      }

      this.setState({
        questionId: questionID,
        questionIndex: questionID - 1,
        sleepRestartConfirmRequested: false,
        sleepRestartAccepted: false,
        aiCheckLoading: false,
        aiCheckAttempts: 0,
        aiGuidance: "",
        lastCheckedAnswer: null,
        lastCheckedProceed: null,
        coachSlotMinHeight: 0,
      });
    }

    this.validateCookies();
  }

  // redirectPastAutoAnsweredQuestion sends the patient past questionID when it
  // is auto-answered. Returns true when it redirected. Direction is inferred
  // from the question they came from, so going back keeps going back rather
  // than bouncing them forward into a loop they can't escape.
  redirectPastAutoAnsweredQuestion = (questionID, fromQuestionID) => {
    const questionnaire = this.consultIntakeQuestionnaire();
    if (
      !isAutoAnswered(questionnaire, this.state.consultAnswers, questionID - 1)
    ) {
      return false;
    }

    const direction = fromQuestionID > questionID ? -1 : 1;
    const { questionId: target } = skipAutoAnsweredQuestions(
      questionnaire,
      this.state.consultAnswers,
      questionID,
      direction,
    );
    if (target === questionID) {
      return false; // nowhere to go — show it rather than loop
    }

    this.props.history.replace(
      `/visits/${this.state.consultationType}/${target}?checkoutSessionID=${this.state.checkoutSessionID}`,
    );
    return true;
  };

  checkForUnsavedChanges = () => {
    // Get saved answer for current question
    const savedAnswers = getConsultAnswersJSON();
    const versionKey = `${this.state.consultationType}-${
      this.consultIntakeQuestionnaire().version
    }`;
    const savedAnswer = savedAnswers?.[versionKey]?.[this.state.questionIndex];
    const currentAnswer = this.state.consultAnswers[this.state.questionIndex];

    if (currentAnswer?.type === "text") {
      return !isEqual(savedAnswer?.value, currentAnswer?.value);
    } else {
      // Exclude 'question' property from comparison since it's metadata
      // that can change (e.g., labels with HTML) without user interaction
      return !isEqual(
        omit(savedAnswer, "question"),
        omit(currentAnswer, "question"),
      );
    }
  };

  resetAnswerForQuestion = (questionIndex) => {
    // Get saved answer for the specified question
    const savedAnswers = getConsultAnswersJSON();
    const versionKey = `${this.state.consultationType}-${
      this.consultIntakeQuestionnaire().version
    }`;
    const savedAnswer = savedAnswers?.[versionKey]?.[questionIndex];

    // Reset the answer to saved state
    if (savedAnswer !== undefined) {
      this.setState((prevState) => ({
        consultAnswers: {
          ...prevState.consultAnswers,
          [questionIndex]: savedAnswer,
        },
      }));
    } else {
      // Clear the answer if no saved state
      this.setState((prevState) => {
        const newAnswers = { ...prevState.consultAnswers };
        delete newAnswers[questionIndex];
        return { consultAnswers: newAnswers };
      });
    }
  };

  totalQuestionsCount = () => {
    let totalCount = this.consultIntakeQuestionnaire().questions.length;
    return totalCount;
  };

  consultIntakeQuestionnaire = () =>
    getConsultationQuestionnaire(
      this.state.consultationType,
      this.state.checkoutSessionID,
    );

  isInitialVisit = () => {
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        return false;
    }
    return true;
  };

  validateAllQuestionsAnswered = () => {
    if (process.env.NODE_ENV === "development") {
      return -1;
    }
    const questions = this.consultIntakeQuestionnaire().questions;

    for (let i = 0; i < questions.length; i++) {
      if (this.state.consultAnswers[i] === undefined) {
        return i;
      }
    }

    return -1;
  };

  saveIntake = () => {
    if (this.intakeSubmissionInFlight) {
      return;
    }
    const unansweredQuestionIdx = this.validateAllQuestionsAnswered();
    if (unansweredQuestionIdx !== -1) {
      this.props.history.push(
        `/visits/${this.state.consultationType}/${
          unansweredQuestionIdx + 1
        }?checkoutSessionID=${this.state.checkoutSessionID}`,
      );
      return;
    }

    // A blood-pressure pair that turned inconsistent after the fact (the
    // patient went back, changed the top number, then jumped ahead by URL) is
    // sent back to the diastolic page rather than saved — that page blocks
    // Continue on it (see handleContinue).
    const bloodPressureMixUpIdx = firstBloodPressureMixUpIndex(
      this.consultIntakeQuestionnaire(),
      this.state.consultAnswers,
    );
    if (bloodPressureMixUpIdx !== -1) {
      this.props.history.push(
        `/visits/${this.state.consultationType}/${
          bloodPressureMixUpIdx + 1
        }?checkoutSessionID=${this.state.checkoutSessionID}`,
      );
      return;
    }

    if (this.sleepRestartNeedsAcceptance()) {
      this.setState({ sleepRestartConfirmRequested: true });
      return;
    }

    this.intakeSubmissionInFlight = true;
    this.setState({
      intakeSubmitting: true,
      sleepRestartConfirmRequested: false,
    });

    const path = `/visits/questionnaire/${this.state.consultationType}/${
      this.consultIntakeQuestionnaire().version
    }/${getVisitID(this.state.checkoutSessionID) || ""}`;

    KickClient.newAuthenticatedClient()
      .post(path, this.state.consultAnswers, {
        headers: this.state.sleepRestartTerms?.disclosure
          ? {
              "X-Sleep-Restart-Accepted": String(
                this.state.sleepRestartAccepted,
              ),
              "X-Sleep-Restart-Hash": this.state.sleepRestartTerms.hash,
            }
          : {},
      })
      .then((response) => {
        // store visit ID
        const { visit, subscriptions } = response.data;
        setVisitID(visit.id, this.state.checkoutSessionID);
        // Intake does not resume billing. Paused members retain their plan and
        // use the existing membership route while awaiting doctor review.
        const planSubscriptions = {};
        subscriptions?.forEach((sub) => {
          if (
            subscriptionGrantsFollowUpAccess(sub.attributes) ||
            (isConsultationTypeInsomniaFollowUp(this.state.consultationType) &&
              sub.attributes.subType === SUBSCRIPTION_SUB_TYPE_SLEEP &&
              sub.attributes.status === DB_STATUS_PAUSED)
          ) {
            planSubscriptions[sub.attributes.subType] = preferLiveSubscription(
              planSubscriptions[sub.attributes.subType],
              { id: sub.id, ...sub.attributes },
            );
          }
        });

        Object.entries(planSubscriptions).forEach(([subType, sub]) =>
          setActiveSubscriptionBySubType(subType, sub),
        );

        // Set cookies from response data for downstream pages
        if (visit?.attributes?.orderType) {
          setVisitProduct(
            visit.attributes.orderType,
            this.state.checkoutSessionID,
          );
        }
        if (isConsultationTypeInsomniaFollowUp(this.state.consultationType)) {
          setFollowUpVisitFlag("yes", this.state.checkoutSessionID);
          const orderType = visit?.attributes?.orderType || "";
          if (isPharmacyPickupOrderType(orderType)) {
            setIsPickup(true);
          }
        }

        if (getForceFlavoredBBs(this.state.checkoutSessionID)) {
          return this.props.history.push(
            `/visits/${this.state.consultationType}/select-num-packs-flavored?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }

        // upgrading to 2 pack --> go straight to select flavored
        if (
          getUpgradingTo2Pack(this.state.checkoutSessionID) &&
          getVisitProduct(this.state.checkoutSessionID) ===
            ORDER_TYPE_PROP_2_PACK_FOLLOW_UP
        ) {
          return this.props.history.push(
            `/visits/${CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP}/select-flavored?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }

        if (isConsultationTypeInsomniaFollowUp(this.state.consultationType)) {
          // Derive routing from response data: check orderType and subscriptions. A sleep sub
          // grants the existing-plan route when active or paused. The doctor approval
          // decides coverage and billing; intake never routes paused members to a new checkout.
          const orderType = visit?.attributes?.orderType || "";
          const hasCoveredSleepSub = subscriptions?.some(
            (sub) =>
              sub.attributes.subType === SUBSCRIPTION_SUB_TYPE_SLEEP &&
              (subscriptionGrantsFollowUpAccess(sub.attributes) ||
                sub.attributes.status === DB_STATUS_PAUSED),
          );
          const shouldGoToPharmacy =
            !orderType ||
            isPharmacyPickupOrderType(orderType) ||
            !hasCoveredSleepSub;

          if (shouldGoToPharmacy) {
            this.props.history.push(
              `/visits/${this.state.consultationType}/select-pharmacy?checkoutSessionID=${this.state.checkoutSessionID}`,
            );
          } else {
            this.props.history.push(
              `/visits/${this.state.consultationType}/confirm-shipping-details?checkoutSessionID=${this.state.checkoutSessionID}`,
            );
          }
        } else if (
          // initial insomnia visit, go to membership description
          isConsultationTypeInsomniaInitial(this.state.consultationType)
        ) {
          this.props.history.push(
            `/visits/${this.state.consultationType}/sleep-membership-description?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else if (this.state.isAddon) {
          // scopolamine visit, go to payment
          const mainConsultationType = getConsultationTypeByProduct(
            getVisitProduct(this.state.checkoutSessionID),
          ); // get original consultation type
          this.props.history.push(
            `/visits/${mainConsultationType}/payment/order-details?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else if (
          this.state.consultationType ===
          CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP
        ) {
          this.props.history.push(
            `/visits/${this.state.consultationType}/select-ga-product?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else {
          // performance anxiety visit, go to select pharmacy
          this.props.history.push(
            `/visits/${this.state.consultationType}/select-pharmacy?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }
      })
      .catch((error) => {
        // The backend rejects a duplicate in-progress family visit at intake save (the
        // insomnia-follow-up shortcut creates the visit here, before any checkout). Route to
        // the portal exactly like the mount guard would — this is an expected business
        // outcome, not an exception worth paging on.
        if (error?.response?.data?.code === "pending_visit_exists") {
          window.location.href = "/me/portal?notice=pending_visit";
          return;
        }

        // The server refused a follow-up intake because the account has no
        // prior initial visit — steer the patient into the initial funnel.
        // Like pending_visit_exists above, an expected business outcome (the
        // mount guard fails open), not an exception worth paging on.
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.code === "no_prior_initial_visit"
        ) {
          alert(
            "It looks like you haven't completed an initial visit with us yet, so we'll start you with the initial visit questionnaire.",
          );
          redirectToInitialFunnel(
            this.state.consultationType,
            this.state.checkoutSessionID,
          );
          return;
        }

        bugsnagClient.user = {
          email: getVisitEmail(),
        };

        bugsnagClient.notify(error, {
          beforeSend: function (report) {
            report.metaData = {
              cookies: Cookies.get(),
              localStorage: localStorage,
              visit: { email: getVisitEmail() },
            };
          },
        });

        // at this point, if code is 401, we need to redirect to login and then back to the questionnaire at the current question
        if (error?.response?.status === 401) {
          window.location.href = `/me/login?redirect=/visits/${this.state.consultationType}/${this.state.questionId}?checkoutSessionID=${this.state.checkoutSessionID}`;
        } else {
          this.setState({
            questionError: (
              <>
                There was an error saving your intake. Please try again or
                please retry filling out the intake by{" "}
                <a
                  style={{ color: "white", fontWeight: "bold" }}
                  href={`${window.location.origin}/visits/${this.state.consultationType}/1?checkoutSessionID=${this.state.checkoutSessionID}`}
                >
                  clicking here
                </a>
                .
              </>
            ),
          });
          console.log("Error saving intake: ", error);
        }
      })
      .finally(() => {
        this.intakeSubmissionInFlight = false;
        if (!this.questionnaireUnmounted) {
          this.setState({ intakeSubmitting: false });
        }
      });
  };

  // Store the answers so far in local storage — what a refresh, a resume and
  // the unsaved-changes navigation guard (checkForUnsavedChanges) read.
  persistConsultAnswers = () => {
    const allAnswers = getConsultAnswersJSON();
    setConsultAnswersJSON({
      ...allAnswers,
      [`${this.state.consultationType}-${
        this.consultIntakeQuestionnaire().version
      }`]: this.state.consultAnswers,
    });
  };

  // "change your top number" on the blood-pressure mix-up note. There is no
  // in-app back button, and the navigation guard alerts on any unsaved answer,
  // so the (still conflicting) diastolic choice is persisted first. That is
  // only local storage: the diastolic page re-flags the pair on the way
  // forward, and saveIntake refuses it.
  goBackToQuestion = (questionIndex) => {
    this.persistConsultAnswers();
    this.setState({ questionError: "" });
    this.props.history.push(
      `/visits/${this.state.consultationType}/${
        questionIndex + 1
      }?checkoutSessionID=${this.state.checkoutSessionID}`,
    );
    window.scrollTo(0, 100);
  };

  nextQuestion = () => {
    this.setState({ questionError: "" });
    this.persistConsultAnswers();

    console.log("getConsultAnswersJSON()", getConsultAnswersJSON());
    console.log("visitID", getVisitID(this.state.checkoutSessionID));
    console.log("this.state.consultationType", this.state.consultationType);

    if (
      this.state.questionId >=
      this.consultIntakeQuestionnaire().questions.length
    ) {
      // persist to db
      this.saveIntake();
    } else {
      // Just go to next question, skipping any the patient's earlier answers
      // have already decided for them (see utils/intakeAutoAnswer.js) and
      // recording those answers as we pass.
      const { questionId: skippedTo, autoAnswers } = skipAutoAnsweredQuestions(
        this.consultIntakeQuestionnaire(),
        this.state.consultAnswers,
        Number(this.state.questionId) + 1,
        1,
      );

      const newQuestionId = skippedTo;
      const newAnswers = { ...this.state.consultAnswers, ...autoAnswers };

      if (Object.keys(autoAnswers).length) {
        // Persist alongside the patient's own answers so a refresh or resume
        // sees the same intake the flow just produced.
        const allAnswers = getConsultAnswersJSON();
        setConsultAnswersJSON({
          ...allAnswers,
          [`${this.state.consultationType}-${
            this.consultIntakeQuestionnaire().version
          }`]: newAnswers,
        });
      }

      // Keep the last visible question mounted while final consent is collected.
      if (newQuestionId > this.consultIntakeQuestionnaire().questions.length) {
        this.setState({ consultAnswers: newAnswers }, this.saveIntake);
        return;
      }

      this.setState({
        consultAnswers: newAnswers,
        questionId: newQuestionId,
        questionIndex: newQuestionId - 1,
        questionError: "",
        aiCheckAttempts: 0,
        aiGuidance: "",
        lastCheckedAnswer: null,
        lastCheckedProceed: null,
        coachSlotMinHeight: 0,
      });

      this.props.history.push(
        `/visits/${this.state.consultationType}/${newQuestionId}?checkoutSessionID=${this.state.checkoutSessionID}`,
      );

      window.scrollTo(0, 100);
    }
  };

  // Continue-button handler. Runs the pre-existing synchronous validation for
  // every question; the AI sufficiency check only engages for questions flagged
  // aiSufficiencyCheck on the PA-initial funnel — every other question and
  // consultation type keeps the exact synchronous path it had before.
  handleContinue = async () => {
    if (
      this.state.sleepRestartConfirmRequested ||
      this.intakeSubmissionInFlight
    ) {
      return;
    }

    // question never answered
    if (this.state.consultAnswers[this.state.questionIndex] == null) {
      this.setState({ questionError: "Please answer the question" });
      return;
    }

    const question =
      this.consultIntakeQuestionnaire().questions[this.state.questionIndex];
    const answer = this.state.consultAnswers[this.state.questionIndex];

    var validAnswers = this.validateQuestionAnswers(question, answer);
    if (!validAnswers) {
      return;
    }

    // Diastolic page only: a bottom number that can't sit below the top number
    // (see utils/bloodPressureCheck.js). The note under the question has been
    // explaining it since the choice was made; flag the choices and stay put.
    if (
      findBloodPressureMixUp(
        this.consultIntakeQuestionnaire(),
        this.state.consultAnswers,
        this.state.questionIndex,
      )
    ) {
      posthog.capture("intake_blood_pressure_mixup_blocked", {
        consultation_type: this.state.consultationType,
        question_code: question.code,
      });
      this.setState({
        questionError: "Please fix your blood pressure answers to continue.",
      });
      return;
    }

    // Systolic page: the extreme-reading note under the question is advisory.
    // Count the patients who continue past it (the answer itself stays out of
    // PostHog, as with the mix-up event above).
    if (
      findSystolicWarning(
        this.consultIntakeQuestionnaire(),
        this.state.consultAnswers,
        this.state.questionIndex,
      )
    ) {
      posthog.capture("intake_blood_pressure_warning_continued", {
        consultation_type: this.state.consultationType,
        question_code: question.code,
      });
    }

    if (!questionNeedsAiCheck(question, this.state.consultationType)) {
      this.nextQuestion();
      return;
    }

    await this.runAiSufficiencyCheck(question, answer);
  };

  // The AI gate. Every failure path proceeds (fail open) — only an explicit
  // enforce-mode insufficient verdict blocks, and the message shown is fixed
  // retry copy, never model text. After AI_CHECK_MAX_ATTEMPTS checked answers
  // the next edited attempt goes through with no validation at all. See
  // docs/pa-chief-complaint-realtime-check.md.
  runAiSufficiencyCheck = async (question, answer) => {
    if (this.state.aiCheckLoading) {
      return;
    }

    const answerText = answer?.value || "";

    // Unchanged text since the last verdict — reuse it, no network call. When
    // the last verdict blocked, the guidance is already on screen.
    if (this.state.lastCheckedAnswer === answerText) {
      if (this.state.lastCheckedProceed) {
        this.nextQuestion();
      }
      return;
    }

    // Client-side cap (the server backstops the same number): stop checking
    // and let the patient through.
    if (this.state.aiCheckAttempts >= AI_CHECK_MAX_ATTEMPTS) {
      this.nextQuestion();
      return;
    }

    const attempt = this.state.aiCheckAttempts + 1;
    const startedAt = Date.now();
    // Pin the slot to its current height while the check runs, so swapping a
    // taller suggestion for the shorter "reading" callout can't make the
    // Continue button jump upward under the pointer that just pressed it.
    this.setState({
      aiCheckLoading: true,
      coachSlotMinHeight: this.coachSlotRef.current
        ? this.coachSlotRef.current.offsetHeight
        : 0,
    });
    posthog.capture("pa_answer_check_requested", {
      consultation_type: this.state.consultationType,
      question_code: question.code,
      attempt,
      answer_length: answerText.length,
    });

    let response = null;
    try {
      response = await assessAnswer({
        consultationType: this.state.consultationType,
        questionCode: question.code,
        answerText,
        attempt,
      });
    } catch (error) {
      posthog.capture("pa_answer_check_error", {
        error_kind: classifyAssessError(error),
        attempt,
      });
      this.setState({
        aiCheckLoading: false,
        aiCheckAttempts: attempt,
        lastCheckedAnswer: answerText,
        lastCheckedProceed: true,
        aiGuidance: "",
        coachSlotMinHeight: 0,
      });
      this.nextQuestion();
      return;
    }

    const outcome = resolveAssessOutcome({ response, attempts: attempt });
    posthog.capture("pa_answer_check_result", {
      consultation_type: this.state.consultationType,
      question_code: question.code,
      attempt,
      answer_length: answerText.length,
      checked: response?.checked,
      sufficient: response?.sufficient,
      mode: response?.mode,
      assessor_version: response?.assessor_version,
      client_latency_ms: Date.now() - startedAt,
    });

    this.setState({
      aiCheckLoading: false,
      aiCheckAttempts: attempt,
      lastCheckedAnswer: answerText,
      lastCheckedProceed: outcome.proceed,
      aiGuidance: outcome.proceed ? "" : outcome.guidance,
      // A new suggestion defines its own (usually taller) height; a proceed
      // navigates away — either way the pin is no longer needed.
      coachSlotMinHeight: 0,
    });

    if (outcome.proceed) {
      this.nextQuestion();
      return;
    }

    posthog.capture("pa_answer_check_blocked", {
      attempt,
      message_variant: attempt === 1 ? "first" : "second",
    });
  };

  validateQuestionAnswers(question, answer) {
    const topLevelValid = this.validateAnswerCompleted(
      question.type,
      answer,
      question.minLength,
    );

    if (!topLevelValid) {
      this.setState({ questionError: "Please answer the question" });
      return false;
    }

    if (!find(question.choices, "children")) return true;

    switch (question.type) {
      case "checkbox": {
        var checkedAnswers = omit(answer, "question");

        const arrValid = map(keys(checkedAnswers), (key) => {
          if (checkedAnswers[key].checked == false) return null;
          if (!question.choices[key].children) return null;

          // childAnswers should be defined if there's been any activity on child
          if (!checkedAnswers[key].childAnswers) {
            this.setState({
              questionError: "Please answer all the follow-on questions",
            });
            return false;
          }

          return map(checkedAnswers[key].childAnswers, (subQ) => {
            return this.validateAnswerCompleted(subQ.type, subQ);
          });
        });

        if (includes(flatten(arrValid), false)) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }
        break;
      }
      case "radio": {
        // no child questions, we're done since top level answer is valid
        if (isUndefined(question.choices[answer.selectedIndex].children))
          return true;

        const childQuestions = question.choices[answer.selectedIndex].children;
        // none of child questions have been answered - invalid so return false
        if (isUndefined(answer[answer.selectedIndex])) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }

        const childAnswers = answer[answer.selectedIndex].childAnswers;
        // if (isUndefined(childAnswers)) {
        //   this.setState({
        //     questionError: "Please answer all the follow-on questions"
        //   });
        //   return false;
        // }

        const arrRadioValid = map(childQuestions, (childQ) => {
          // childAnswers should be defined if there's been any activity on child
          if (isUndefined(childAnswers[childQ.code])) {
            console.error("QCode not found in answer");
            return false;
          }

          // Validate child answer itself
          const childValid = map(childAnswers, (subQ) => {
            return this.validateAnswerCompleted(subQ.type, subQ);
          });

          // Check for grandchildren (3rd level) if child is a radio question
          if (childQ.type === "radio") {
            const childAnswer = childAnswers[childQ.code];
            if (childAnswer?.selectedIndex !== undefined) {
              const selectedChildChoice =
                childQ.choices[childAnswer.selectedIndex];

              // If selected choice has children (grandchildren), validate them
              if (selectedChildChoice?.children) {
                const grandchildQuestions = selectedChildChoice.children;
                const grandchildAnswers =
                  childAnswer[childAnswer.selectedIndex]?.childAnswers;

                if (isUndefined(grandchildAnswers)) {
                  this.setState({
                    questionError: "Please answer all the follow-on questions",
                  });
                  return false;
                }

                const grandchildValid = map(
                  grandchildQuestions,
                  (grandchildQ) => {
                    if (isUndefined(grandchildAnswers[grandchildQ.code])) {
                      console.error("Grandchild QCode not found in answer");
                      return false;
                    }

                    return this.validateAnswerCompleted(
                      grandchildQ.type,
                      grandchildAnswers[grandchildQ.code],
                    );
                  },
                );

                if (includes(grandchildValid, false)) {
                  this.setState({
                    questionError: "Please answer all the follow-on questions",
                  });
                  return false;
                }
              }
            }
          }

          return childValid;
        });

        if (includes(flatten(arrRadioValid), false)) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }
        break;
      }
      case "textarea": {
        if (answer.value.length < question.minLength) {
          this.setState({
            questionError: "Please answer the question",
          });
          return false;
        }
        break;
      }
    }
    return true;
  }

  validateAnswerCompleted(questionType, answer, minLength = 1) {
    switch (questionType) {
      case "checkbox": {
        const checkedBox = findKey(answer, (o) => {
          return o.checked == true;
        });

        return !isUndefined(checkedBox);
      }
      case "radio":
      case "bloodpressure-systolic":
      case "bloodpressure-diastolic":
        return !isUndefined(answer.selectedIndex);

      case "text":
      case "textarea": {
        const trimmedValue = answer.value?.replace(/[^a-zA-Z0-9]/g, "");
        const isValid = trimmedValue.length >= minLength;
        return isValid;
      }
    }
  }

  recordAnswer = (questionIndex, answer) => {
    console.log(
      "recordAnswer",
      questionIndex,
      answer,
      this.state.consultAnswers,
    );
    console.log("visitProduct", getVisitProduct(this.state.checkoutSessionID));
    const newConsultAnswers = {
      ...this.state.consultAnswers,
      [questionIndex]: answer,
    };
    this.setState({
      consultAnswers: newConsultAnswers,
      questionError: "",
      refresh: !this.state.refresh,
    });
  };

  render() {
    if (this.state.invalidConsultationType) {
      // redirectToFunnelStart() is in flight (see the constructor).
      return null;
    }
    const { classes, location } = this.props;
    const questionnaire = this.consultIntakeQuestionnaire();
    const question = questionnaire.questions[this.state.questionIndex];
    // Shown the moment a conflicting bottom number is picked, not only on
    // Continue — the top number is on the previous page, so the patient needs
    // both quoted to see the mistake.
    const bloodPressureMixUp = findBloodPressureMixUp(
      questionnaire,
      this.state.consultAnswers,
      this.state.questionIndex,
    );
    // Systolic page: a top number in the highest or lowest bucket gets an
    // "are you sure?" note the moment it's picked. Advisory only — Continue
    // still works (see utils/bloodPressureCheck.js).
    const systolicWarning = findSystolicWarning(
      questionnaire,
      this.state.consultAnswers,
      this.state.questionIndex,
    );

    return (
      <React.Fragment>
        {isConsultationTypeInsomniaFollowUp(this.state.consultationType) &&
          this.state.sleepRestartConfirmRequested && (
            <SleepFollowUpBillingNotice
              terms={this.state.sleepRestartTerms}
              accepted={this.state.sleepRestartAccepted}
              loading={!this.state.sleepRestartTermsLoaded}
              onAccept={this.acceptSleepRestart}
              onDecline={this.declineSleepRestart}
              error={this.state.sleepRestartError}
            />
          )}
        <VisitProgress
          questionNumber={this.state.questionId}
          totalQuestions={this.totalQuestionsCount()}
        />
        <div
          className={classes.container}
          style={{
            backgroundColor: COLOR_KICK_BLUE,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Card
            className={classes.cardConsultation}
            style={{
              backgroundColor: COLOR_KICK_BLUE,
              boxShadow: "none",
              maxWidth: "900px",
              padding: "30px 0",
            }}
          >
            <CardBody style={{ border: "" }}>
              <GridContainer justifyContent="center">
                <GridItem xs={12} sm={12} md={8} noPadding>
                  <ConsultationQuestion
                    location={location}
                    questionIndex={this.state.questionIndex}
                    questionError={this.state.questionError}
                    question={question}
                    consultAnswers={this.state.consultAnswers}
                    refresh={this.state.refresh}
                    recordAnswer={this.recordAnswer}
                    coachSlotId={
                      questionNeedsAiCheck(
                        question,
                        this.state.consultationType,
                      )
                        ? "intake-coach"
                        : undefined
                    }
                  />

                  {bloodPressureMixUp && (
                    <IntakeWarningNote
                      text={bloodPressureMixUpMessage(bloodPressureMixUp)}
                      onAction={() =>
                        this.goBackToQuestion(bloodPressureMixUp.systolicIndex)
                      }
                    />
                  )}

                  {systolicWarning && (
                    <IntakeWarningNote
                      text={systolicWarningMessage(systolicWarning)}
                    />
                  )}

                  {/* Quiet callout slot for the AI answer check. minHeight is
                      pinned to the previous callout's height while a re-check
                      is in flight so the Continue button never jumps upward
                      under a cursor/thumb mid-tap. aria-live announces state
                      changes to screen readers; the textarea points here via
                      aria-describedby. */}
                  <div
                    ref={this.coachSlotRef}
                    id="intake-coach"
                    aria-live="polite"
                    style={{ minHeight: this.state.coachSlotMinHeight }}
                  >
                    {this.state.aiCheckLoading ? (
                      <AiCoachCallout state="reading" />
                    ) : this.state.aiGuidance ? (
                      <AiCoachCallout
                        state="suggestion"
                        text={this.state.aiGuidance}
                      />
                    ) : null}
                  </div>

                  <div style={{ color: "red", margin: "20px auto 6px auto" }}>
                    <p style={{ textAlign: "center" }}>
                      {this.state.questionError}
                    </p>
                  </div>

                  <Button
                    onClick={this.handleContinue}
                    disabled={
                      this.state.aiCheckLoading ||
                      this.state.intakeSubmitting ||
                      this.state.sleepRestartConfirmRequested
                    }
                    style={{
                      display: "block",

                      backgroundColor: kickRed,
                      margin: "0px auto",
                      width: "300px",
                      fontSize: "18px",
                      fontWeight: "medium",
                      textTransform: "unset",
                    }}
                  >
                    {this.state.intakeSubmitting
                      ? "Submitting…"
                      : this.state.sleepRestartConfirmRequested &&
                          !this.state.sleepRestartTermsLoaded &&
                          !this.state.sleepRestartError
                        ? "Preparing submission…"
                        : "Continue"}
                  </Button>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </React.Fragment>
    );
  }
}

Consultation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withApollo(withStyles(consultationStyles)(Consultation));
